import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'gatsby';

import { useTranslation } from 'react-i18next';
import useWithGA from '../hooks/useWithGA';

import useNavigation from '../hooks/useNavigation';
import useSearch from '../hooks/useSearch';
import useTransformGatsbyData from '../hooks/useTransformGatsbyData';
import useWithCategory from '../hooks/useWithCategory';

import { CategoryTop } from '../ui/templates/categoryTop/CategoryTop';

import useStrapiPageData from '../hooks/data/useStrapiPageData';

// import './categoryTopTemplate.scss';
import MetatagsForLayout from '../components/MetatagsForLayout';
import { removeHTMLAndLineBreak } from '../utils/utils';
import { isLocaleSweden } from '../utils/locale';

import { Robot } from '../../static/icons/robot';

import {
  PAGE_SYSTEMGUIDE,
  PAGE_SYSTEM,
  PAGE_INDEX,
  PAGE_CATEGORIES,
} from '../constants/strapi';
import CONSTANTS_NUMBERS from '../constants/numbers';

export const query = graphql`
  query CategoryTopData(
    $pageBuildId: String!
    $topCategory: String!
    $systemIds: [String]
  ) {
    topCategory: bwTopCategory(id: { eq: $topCategory }) {
      data
    }
    systemsInTopCategory: allBwSystem(filter: { id: { in: $systemIds } }) {
      nodes {
        data
      }
    }
    site {
      siteMetadata {
        siteUrl
      }
    }
    bwPageBuildData(id: { eq: $pageBuildId }) {
      data
    }
  }
`;

const CategoryTopPage = ({ location, data, pageContext }) => {
  const { topCategory, locale } = pageContext;
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const { siteUrls } = bwPageBuildData.pageData;

  const { menu, footer } = useNavigation(siteUrls, location);
  const search = useSearch();
  const { savePageView, handleDataLayer } = useWithGA();
  const { t } = useTranslation();
  const [systemguidePageUrl] = useStrapiPageData(PAGE_SYSTEMGUIDE);
  const [systemPageUrl] = useStrapiPageData(PAGE_SYSTEM);
  const [categoriesPageUrl] = useStrapiPageData(PAGE_CATEGORIES);

  const topCategoryData = useTransformGatsbyData(data.topCategory);
  const systemsInTopCategory = useTransformGatsbyData(
    data.systemsInTopCategory
  );

  const { allSystems } = useWithCategory(topCategory, systemsInTopCategory, []);

  const {
    title,
    categoryName,
    faq1Title,
    faq1Text,
    faq2Title,
    faq2Text,
    topSectionTitle,
    topSectionText,
    // linkText,
    // linkUrl,
    articleTitle,
    articleText,
    mainCategories: categories,
  } = topCategoryData;

  // let hasFaq = false;
  let textOver = [];
  if (faq1Title && faq2Title) {
    // hasFaq = true;
    textOver = [
      {
        title: faq1Title,
        text: faq1Text,
      },
      {
        title: faq2Title,
        text: faq2Text,
      },
    ];
  } else {
    textOver = [
      {
        title: topSectionTitle,
        text: topSectionText,
      },
    ];
  }

  const categoriesObj = {};
  categories.forEach((catObj) => {
    const { categoryDbName } = catObj;

    categoriesObj[categoryDbName] = {
      ...catObj,
    };
  });

  useEffect(() => {
    savePageView({ type: 'TOPCATEGORY', ids: topCategory });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const categoryLinks = categories
    ?.sort(
      (a, b) => b.tier - a.tier || a.categoryName.localeCompare(b.categoryName)
    )
    .map((catObj) => {
      const { categoryName: catName, categoryDbName, slugi18n } = catObj;
      return {
        id: categoryDbName,
        name: catName,
        url: `/${slugi18n}/`,
      };
    });
  const isSweden = isLocaleSweden(locale);

  const sectionTopCard = {
    title: isSweden ? t('common.helpSystemGuide') : t('common.trySystemGuide'),
    description: t('common.systemGuideAlgorithmsText', {
      number: CONSTANTS_NUMBERS.guideRuns,
    }),
    url: systemguidePageUrl,
    icon: <Robot color="#297FFF" width="5" />,
    label: t('common.systemGuide'),
    buttonText: t('common.trySystemGuide'),
    handleClick: () => {
      handleDataLayer({
        event_name: 'to_systemguide',
        page_type: 'Top category page',
        system_category: topCategory || '',
        type: 'CTA',
      });
    },
  };

  return (
    <CategoryTop
      hero={{
        title,
        card: sectionTopCard,
      }}
      breadcrumbs={[
        {
          title: t('common.home'),
          href: '/',
        },
        {
          title: categoryName,
          // href: `/${categoryUrl}/`,
        },
      ]}
      sections={[
        {
          sectionType: 'single',
          component: {
            componentType: 'sectionOne',
            type: 'sectionOne',
            // card: sectionTopCard,
            componentData: textOver,
          },
        },
        {
          sectionType: 'single',
          component: {
            componentType: 'sectionSystemList',
            type: 'sectionSystemList',
            componentData: {
              totalSystems: allSystems.length,
              categoryName,
              systemPageUrl,
              systems: allSystems,
              // compare,
              // sorting,
              // handleSortChange,
            },
          },
        },
        {
          sectionType: 'single',
          component: {
            componentType: 'sectionThree',
            type: 'sectionThree',
            componentData: [
              {
                title: articleTitle,
                text: articleText,
              },
            ],
          },
        },
        {
          sectionType: 'single',
          component: {
            type: 'popularCategories',
            title: t('common.categories'),
            linkUrl: categoriesPageUrl,
            componentData: categoryLinks,
          },
        },
      ]}
      t={t}
      locale={locale}
      menu={menu}
      footer={footer}
      siteUrls={siteUrls}
      location={location}
      search={search}
    />
  );
};

CategoryTopPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  data: PropTypes.shape({}).isRequired,
  pageContext: PropTypes.shape({
    category: PropTypes.string,
    systemsData: PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      systemsTopInCats: PropTypes.arrayOf(PropTypes.object),
    }),
  }).isRequired,
};

export default CategoryTopPage;

export function Head({ data, location, pageContext }) {
  const { site } = data;
  const { locale, noindex } = pageContext;
  const { t } = useTranslation();
  const homePageData = useStrapiPageData(PAGE_INDEX)[1];
  const ldJsonArray = [];
  const faqForLdJson = [];
  const homeTextForBreadCrumb = t('common.home');

  const topCategoryData = useTransformGatsbyData(data.topCategory);
  const bwPageBuildData = useTransformGatsbyData(data.bwPageBuildData);
  const { siteUrls } = bwPageBuildData.pageData;

  const { seo, categoryName, faq1Title, faq1Text, faq2Title, faq2Text } =
    topCategoryData;

  const faq1TextWithoutHTML = removeHTMLAndLineBreak(faq1Text);
  const faq2TextWithoutHTML = removeHTMLAndLineBreak(faq2Text);

  ldJsonArray.push({
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: homeTextForBreadCrumb,
        item: {
          '@id': site.siteMetadata.siteUrl,
          name: homePageData.seo.metaTitle,
          description: homePageData.seo.metaDescription,
          url: site.siteMetadata.siteUrl,
          // sameAs: [
          //   'https://businesswith.se',
          //   'https://businesswith.no',
          //   'https://businesswith.dk',
          // ],
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: categoryName,
        item: {
          '@id': site.siteMetadata.siteUrl + location.pathname,
          name: categoryName,
          description: seo?.metaDescription,
          url: site.siteMetadata.siteUrl + location.pathname,
        },
      },
    ],
  });

  if (faq1Title) {
    faqForLdJson.push({
      '@type': 'Question',
      name: faq1Title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq1TextWithoutHTML,
      },
    });
  }

  if (faq2Title) {
    faqForLdJson.push({
      '@type': 'Question',
      name: faq2Title,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq2TextWithoutHTML,
      },
    });
  }

  if (faqForLdJson.length > 0) {
    ldJsonArray.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: faqForLdJson,
    });
  }

  return (
    <>
      <MetatagsForLayout
        title={seo.metaTitle}
        description={seo.metaDescription}
        pathname={location.pathname}
        locale={locale}
        siteUrls={siteUrls}
        noindex={noindex || false}
      />
      <script type="application/ld+json">{JSON.stringify(ldJsonArray)}</script>
    </>
  );
}
